import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  CalendarIcon,
  EditIcon,
  MedicalNotesIcon,
  ShieldIcon,
  MapPinIcon,
  HelpingIcon,
  PrintIcon,
} from "../../../Icons";
import CollapsibleSection from "./CollapsibleSection";
import {
  getPatientAssessment,
  updatePatientAssessment,
} from "../../../services/patientService";
import { patientSectionTones, PATIENT_THEME } from "../patientTheme";
import { assessmentPrintUrl } from "../assessmentPrint";
import {
  ADL_OPTIONS,
  IADL_OPTIONS,
  MOBILITY_OPTIONS,
  CONTINENCE_OPTIONS,
  BEHAVIOR_OPTIONS,
  COGNITIVE_OPTIONS,
  mergeAssessmentPayload,
  pickAssessmentSectionPayload,
  toggleCheckboxValue,
  CLIENT_ASSESSMENT_TITLE,
} from "../assessmentForm.shared";
import {
  CheckboxSection,
  DualCheckboxColumns,
  ScheduleOfCareFields,
  HomeAssessmentFields,
  SafetyAssessmentFields,
} from "./AssessmentFieldKit";
import PatientNotesSection from "./PatientNotesSection";
import { PATIENT_NOTE_TYPE_ASSESSMENT } from "../patientNote.shared";

const SECTION_TONES = patientSectionTones([
  "schedule",
  "adl",
  "iadl",
  "physical",
  "mental",
  "home",
  "safety",
]);

const SectionCard = ({
  title,
  icon,
  tone,
  canEdit,
  editing,
  editLocked,
  saving,
  onEdit,
  onCancel,
  onSave,
  children,
}) => {
  const t = SECTION_TONES[tone] || SECTION_TONES.schedule;
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (editing) setOpen(true);
  }, [editing]);

  const actions = canEdit ? (
    !editing ? (
      editLocked ? null : (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            onEdit?.();
          }}
          className="inline-flex items-center justify-center h-7 w-7 text-indigo-700 bg-white border border-slate-200 rounded hover:bg-slate-50"
          aria-label="Edit"
          title="Edit"
        >
          <EditIcon size={13} />
        </button>
      )
    ) : (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-7 px-2.5 text-xs font-medium text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          className={`inline-flex items-center justify-center gap-1 h-7 px-2.5 text-xs font-medium border bg-white rounded disabled:opacity-50 ${t.edit}`}
        >
          {saving ? (
            <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : null}
          Save
        </button>
      </div>
    )
  ) : null;

  return (
    <CollapsibleSection
      title={title}
      icon={icon}
      tone={t}
      open={open}
      onOpenChange={setOpen}
      actions={actions}
    >
      {children}
    </CollapsibleSection>
  );
};

const PatientAssessmentTab = ({ patientId, chartId, canEdit, canEditNotes }) => {
  const [values, setValues] = useState(() => mergeAssessmentPayload(null));
  const [draft, setDraft] = useState(() => mergeAssessmentPayload(null));
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAssessment = useCallback(async () => {
    if (!patientId || !chartId) return;
    setLoading(true);
    try {
      const res = await getPatientAssessment(patientId, { chartId });
      const payload = mergeAssessmentPayload(
        res?.result?.assessment?.payload || res?.assessment?.payload,
      );
      setValues(payload);
      setDraft(payload);
    } catch (err) {
      toast.error(err.message || "Failed to load assessment");
    } finally {
      setLoading(false);
    }
  }, [patientId, chartId]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  const startEdit = (section) => {
    setDraft(structuredClone(values));
    setEditingSection(section);
  };

  const cancelEdit = () => {
    setDraft(structuredClone(values));
    setEditingSection(null);
  };

  const saveSection = async (section) => {
    const data = pickAssessmentSectionPayload(section, draft);
    if (!data) return;
    setSaving(true);
    try {
      const res = await updatePatientAssessment(patientId, {
        chartId: Number(chartId),
        section,
        data,
      });
      const payload = mergeAssessmentPayload(
        res?.result?.assessment?.payload || res?.assessment?.payload,
      );
      setValues(payload);
      setDraft(payload);
      setEditingSection(null);
      toast.success("Assessment saved");
    } catch (err) {
      toast.error(err.message || "Failed to save assessment");
    } finally {
      setSaving(false);
    }
  };

  const updateDraft = (key, updater) => {
    setDraft((prev) => ({
      ...prev,
      [key]: typeof updater === "function" ? updater(prev[key]) : updater,
    }));
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-slate-100 rounded-lg border border-slate-200" />
        <div className="h-32 bg-slate-100 rounded-xl border border-slate-200" />
      </div>
    );
  }

  const isEditing = (key) => editingSection === key;
  const sectionData = (key) => (isEditing(key) ? draft : values)[key] ?? values[key];

  const sectionProps = (id) => ({
    canEdit,
    editing: editingSection === id,
    editLocked: !!editingSection && editingSection !== id,
    saving: saving && editingSection === id,
    onEdit: () => startEdit(id),
    onCancel: cancelEdit,
    onSave: () => saveSection(id),
  });

  const scheduleData = sectionData("scheduleOfCare");

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!canEdit ? (
          <p className="px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg">
            Assessment is read-only for your role
          </p>
        ) : (
          <span className="hidden sm:block" />
        )}
        <button
          type="button"
          onClick={() => {
            const url = assessmentPrintUrl(patientId, chartId);
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ml-auto ${PATIENT_THEME.button.outline}`}
        >
          <PrintIcon size={13} />
          Print {CLIENT_ASSESSMENT_TITLE}
        </button>
      </div>

      <SectionCard
        title="Schedule of Care"
        icon={<CalendarIcon size={14} />}
        tone="schedule"
        {...sectionProps("scheduleOfCare")}
      >
        <ScheduleOfCareFields
          data={scheduleData}
          canEdit={isEditing("scheduleOfCare")}
          onChange={(next) => updateDraft("scheduleOfCare", next)}
        />
      </SectionCard>

      <SectionCard
        title="Activities of Daily Living (ADL)"
        icon={<HelpingIcon size={14} />}
        tone="adl"
        {...sectionProps("adl")}
      >
        <CheckboxSection
          options={ADL_OPTIONS}
          selected={sectionData("adl").selected}
          otherDetails={sectionData("adl").otherDetails}
          canEdit={isEditing("adl")}
          columns={4}
          onToggle={(opt) =>
            updateDraft("adl", (g) => ({
              ...g,
              selected: toggleCheckboxValue(g.selected, opt),
            }))
          }
          onOtherChange={(v) =>
            updateDraft("adl", (g) => ({ ...g, otherDetails: v }))
          }
        />
      </SectionCard>

      <SectionCard
          title="Instrumental Activities of Daily Living (IADL)"
        icon={<HelpingIcon size={14} />}
        tone="iadl"
        {...sectionProps("iadl")}
      >
        <CheckboxSection
          options={IADL_OPTIONS}
          selected={sectionData("iadl").selected}
          otherDetails={sectionData("iadl").otherDetails}
          canEdit={isEditing("iadl")}
          columns={4}
          onToggle={(opt) =>
            updateDraft("iadl", (g) => ({
              ...g,
              selected: toggleCheckboxValue(g.selected, opt),
            }))
          }
          onOtherChange={(v) =>
            updateDraft("iadl", (g) => ({ ...g, otherDetails: v }))
          }
        />
      </SectionCard>

      <SectionCard
        title="Physical Assessment"
        icon={<MedicalNotesIcon size={14} />}
        tone="physical"
        {...sectionProps("physical")}
      >
        <DualCheckboxColumns
          canEdit={isEditing("physical")}
          leftTitle="Mobility"
          rightTitle="Continence"
          left={{
            options: MOBILITY_OPTIONS,
            selected: sectionData("physical").mobility.selected,
            otherDetails: sectionData("physical").mobility.otherDetails,
            onToggle: (opt) =>
              updateDraft("physical", (p) => ({
                ...p,
                mobility: {
                  ...p.mobility,
                  selected: toggleCheckboxValue(p.mobility.selected, opt),
                },
              })),
            onOtherChange: (v) =>
              updateDraft("physical", (p) => ({
                ...p,
                mobility: { ...p.mobility, otherDetails: v },
              })),
          }}
          right={{
            options: CONTINENCE_OPTIONS,
            selected: sectionData("physical").continence.selected,
            otherDetails: sectionData("physical").continence.otherDetails,
            onToggle: (opt) =>
              updateDraft("physical", (p) => ({
                ...p,
                continence: {
                  ...p.continence,
                  selected: toggleCheckboxValue(p.continence.selected, opt),
                },
              })),
            onOtherChange: (v) =>
              updateDraft("physical", (p) => ({
                ...p,
                continence: { ...p.continence, otherDetails: v },
              })),
          }}
        />
      </SectionCard>

      <SectionCard
        title="Mental Assessment"
        icon={<MedicalNotesIcon size={14} />}
        tone="mental"
        {...sectionProps("mental")}
      >
        <DualCheckboxColumns
          canEdit={isEditing("mental")}
          leftTitle="Behavior"
          rightTitle="Cognitive"
          left={{
            options: BEHAVIOR_OPTIONS,
            selected: sectionData("mental").behavior.selected,
            otherDetails: sectionData("mental").behavior.otherDetails,
            onToggle: (opt) =>
              updateDraft("mental", (m) => ({
                ...m,
                behavior: {
                  ...m.behavior,
                  selected: toggleCheckboxValue(m.behavior.selected, opt),
                },
              })),
            onOtherChange: (v) =>
              updateDraft("mental", (m) => ({
                ...m,
                behavior: { ...m.behavior, otherDetails: v },
              })),
          }}
          right={{
            options: COGNITIVE_OPTIONS,
            selected: sectionData("mental").cognitive.selected,
            otherDetails: sectionData("mental").cognitive.otherDetails,
            onToggle: (opt) =>
              updateDraft("mental", (m) => ({
                ...m,
                cognitive: {
                  ...m.cognitive,
                  selected: toggleCheckboxValue(m.cognitive.selected, opt),
                },
              })),
            onOtherChange: (v) =>
              updateDraft("mental", (m) => ({
                ...m,
                cognitive: { ...m.cognitive, otherDetails: v },
              })),
          }}
        />
      </SectionCard>

      <SectionCard
        title="Home Assessment"
        icon={<MapPinIcon size={14} />}
        tone="home"
        {...sectionProps("home")}
      >
        <HomeAssessmentFields
          data={sectionData("home")}
          canEdit={isEditing("home")}
          onChange={(next) => updateDraft("home", next)}
        />
      </SectionCard>

      <SectionCard
        title="Safety Assessment"
        icon={<ShieldIcon size={14} />}
        tone="safety"
        {...sectionProps("safety")}
      >
        <SafetyAssessmentFields
          data={sectionData("safety")}
          canEdit={isEditing("safety")}
          onToggle={(opt) =>
            updateDraft("safety", (s) => ({
              ...s,
              selected: toggleCheckboxValue(s.selected, opt),
            }))
          }
          onChange={(next) => updateDraft("safety", next)}
        />
      </SectionCard>

      <PatientNotesSection
        patientId={patientId}
        canEditNotes={canEditNotes}
        noteType={PATIENT_NOTE_TYPE_ASSESSMENT}
      />
    </div>
  );
};

export default PatientAssessmentTab;
